import { useEffect, useMemo, useState } from "react";
import { Plus, ArrowRight, Phone, X, Sparkles, AlertTriangle, Clock, LayoutGrid, List as ListIcon, Table as TableIcon, ChevronDown, ChevronRight, MessageSquare, Star, Pencil, CalendarPlus, Search, GripVertical, UserPlus, Globe, FlaskConical, MapPin, ArrowUpDown, Activity, Flame, Snowflake } from "lucide-react";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  closestCorners,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import api from "../lib/api";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import Modal from "../components/Modal";
import CallLog from "../components/CallLog";
import MeetingForm from "../components/MeetingForm";
import { Link, useNavigate } from "react-router-dom";
// Iter 228 — Log a call removed from leads page; not in HV daily flow.
import LeadEditor from "../components/LeadEditor";
import LeadsTableView from "../components/LeadsTableView";
import { useAuth } from "../context/AuthContext";
import { usePersistedState } from "../lib/persisted";
import { LEAD_PHASE_GROUPS as PHASE_GROUPS, LEAD_SOURCES, LEAD_TITLES, PRICE_BRACKETS, PRICE_BRACKET_LABELS } from "../lib/enums";
import DiscoverProspectsDialog from "../components/DiscoverProspectsDialog";
import useUnseen from "../lib/useUnseen";
import useFocusOnMount from "../lib/useFocusOnMount";
import VoiceTextarea from "../components/VoiceTextarea";

const ALL_PHASES = PHASE_GROUPS.flatMap((g) => g.phases);

const empty = {
  customer_id: "",
  customer_name: "",
  customer_phone: "",
  customer_email: "",
  title: "",
  phase: "lead_logged",
  value: 0,
  notes: "",
  lead_source: "",
  lead_rating: "",
  service_interest: "",
  first_contact_date: "",
  next_followup_date: "",
  // Wave 4
  price_bracket: "",
  lead_title_enum: "",
  current_provider: "",
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [open, setOpen] = useState(false);
  const [discoverOpen, setDiscoverOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingLead, setEditingLead] = useState(null); // when set → re-uses the form modal in edit mode
  const [activeLead, setActiveLead] = useState(null);
  const [meetingOpen, setMeetingOpen] = useState(null); // { leadId, customerId } | null
  // (Bulk-import button was removed after the one-time PROSPECT LOG import —
  // that was a one-off task. Admins can still use the backend endpoint
  // /api/leads/import directly for emergencies.)
  const { user } = useAuth();
  const [globalSearch, setGlobalSearch] = useState("");
  const [view, setView] = usePersistedState("leads.view", "auto");
  // [Iter 290zp.17] Lead Activity Sort — `default` keeps the existing
  // per-phase sort_order behaviour. `fresh` sorts by last_activity_at DESC
  // (newest movement first). `stale` sorts ASC (oldest first → leads that
  // need attention). Persists per-device via localStorage.
  const [activitySort, setActivitySort] = usePersistedState("leads.activitySort", "default");
  const [activitySortMenuOpen, setActivitySortMenuOpen] = useState(false);
  const navigate = useNavigate();

  const load = async (_opts) => {
    // Iter 173 — Leads has no loading skeleton; opts unused.
    const [l, c, f] = await Promise.all([
      api.get("/leads"),
      api.get("/customers"),
      api.get("/followups").catch(() => ({ data: [] })),
    ]);
    // [Iter 290zp.19] Defensive — coerce every response to an array.
    // Mirrors the Customers.jsx hotfix; rep users were being crashed by
    // a non-array response (transient proxy / cache / role-scoped 304).
    setLeads(Array.isArray(l.data) ? l.data : []);
    setCustomers(Array.isArray(c.data) ? c.data : []);
    setFollowups(Array.isArray(f.data) ? f.data : []);
  };
  useEffect(() => {
    load();
  }, []);
  // Auto-refresh while the tab is foreground (Wave 19 — concurrent-edit safety).
  useAutoRefresh(load, { intervalMs: 30_000 });

  // Awareness Contract — DESIGN_CONTRACT §7.
  const { isUnseen, touch, dismiss } = useUnseen("leads", leads);
  useEffect(() => { touch(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useFocusOnMount({ ready: leads.length > 0, deps: [leads.length] });

  // ?edit=<leadId> → pop the form modal in edit mode
  useEffect(() => {
    if (!leads.length) return;
    const params = new URLSearchParams(window.location.search);
    const editId = params.get("edit");
    if (editId) {
      const found = leads.find((l) => l.id === editId);
      if (found) {
        setEditingLead(found);
        setForm({
          customer_id: found.customer_id || "",
          customer_name: found.customer_name || "",
          customer_phone: found.customer_phone || "",
          customer_email: found.customer_email || "",
          title: found.title || "",
          phase: found.phase || "lead_logged",
          value: found.value || 0,
          notes: found.notes || "",
          lead_source: found.lead_source || "",
          lead_rating: found.lead_rating || "",
          service_interest: Array.isArray(found.service_interest)
            ? found.service_interest.join(", ")
            : (found.service_interest || ""),
          first_contact_date: found.first_contact_date || "",
          next_followup_date: found.next_followup_date || "",
          price_bracket: found.price_bracket || "",
          lead_title_enum: found.lead_title_enum || "",
          current_provider: found.current_provider || "",
        });
        setOpen(true);
        // Strip ?edit= from URL.
        window.history.replaceState({}, "", window.location.pathname);
      }
    }
  }, [leads]);

  // Follow-up summary buckets
  const followupStats = useMemo(() => {
    const now = Date.now();
    const week = 7 * 86400000;
    const month = 30 * 86400000;
    let due = 0;
    let in7 = 0;
    let in30 = 0;
    let later = 0;
    for (const f of followups) {
      if (f.status !== "pending") continue;
      const t = new Date(f.due_date).getTime();
      if (t <= now) due += 1;
      else if (t <= now + week) in7 += 1;
      else if (t <= now + month) in30 += 1;
      else later += 1;
    }
    return { due, in7, in30, later, total: due + in7 + in30 + later };
  }, [followups]);

  // Global text search — applied to every lead (incl. closed) before any
  // grouping. Empty query = all leads pass through.
  const visibleLeads = useMemo(() => {
    const q = globalSearch.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter((l) => {
      const phaseMeta = ALL_PHASES.find((p) => p.key === l.phase);
      const haystack = [
        l.title,
        l.customer_name,
        l.customer_phone,
        l.customer_email,
        l.contact_name,
        l.notes,
        l.lead_source,
        l.service_interest,
        l.current_provider,
        l.lead_title_enum,
        l.price_bracket,
        l.phase,
        phaseMeta?.label,
        phaseMeta?.group,
        ...(l.secondary_phases || []),
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [leads, globalSearch]);

  // Auto = kanban for ≤50 leads, list for more (Batch C requirement).
  // [2026-02-19] Also force list on mobile — the kanban view is a
  // min-w-1700px horizontal-scroll container and on Android the
  // pan-x gesture blocks vertical scroll, identical to the JobCards
  // bug Dirk reported.
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(max-width: 767px)").matches
      : false,
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setIsMobile(e.matches);
    try { mq.addEventListener("change", onChange); }
    catch { mq.addListener(onChange); }
    return () => {
      try { mq.removeEventListener("change", onChange); }
      catch { mq.removeListener(onChange); }
    };
  }, []);
  const _resolvedView = view === "auto" ? (visibleLeads.length <= 50 ? "kanban" : "list") : view;
  const effectiveView = isMobile && _resolvedView === "kanban" ? "list" : _resolvedView;

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      value: Number(form.value) || 0,
      lead_rating: form.lead_rating ? Number(form.lead_rating) : null,
      service_interest: form.service_interest
        ? form.service_interest.split(",").map((s) => s.trim()).filter(Boolean)
        : null,
      first_contact_date: form.first_contact_date || null,
      next_followup_date: form.next_followup_date || null,
      price_bracket: form.price_bracket || null,
      lead_title_enum: form.lead_title_enum || null,
      current_provider: form.current_provider || null,
    };
    if (editingLead) {
      await api.put(`/leads/${editingLead.id}`, payload);
    } else {
      await api.post("/leads", payload);
    }
    setOpen(false);
    setEditingLead(null);
    setForm(empty);
    load();
  };

  const move = async (id, phase) => {
    await api.patch(`/leads/${id}/phase`, { phase });
    load();
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  // Persist intra-column ordering. Items list passes [{id, sort_order}].
  const persistOrder = async (items, targetPhase) => {
    try {
      await api.patch("/leads/reorder", { items, phase: targetPhase });
    } catch (_e) {
      // best-effort: reload to reconcile if persistence fails
      load();
    }
  };

  // Helper: extract the canonical "last movement" timestamp for sorting.
  // Backend fills this in. We fall back to updated_at / created_at so
  // legacy rows still sort sensibly. Returns 0 for completely orphan
  // rows so they cluster at the "oldest" end with the stale sort.
  const _leadActivityTs = (l) => {
    const v = l?.last_activity_at || l?.updated_at || l?.created_at;
    if (!v) return 0;
    const t = Date.parse(v);
    return Number.isFinite(t) ? t : 0;
  };

  // Helper: build the ordered list for a given phase column (visible leads only).
  const itemsForPhase = (phaseKey) => {
    const list = visibleLeads.filter(
      (l) => l.phase === phaseKey || (l.secondary_phases || []).includes(phaseKey),
    );
    // [Iter 290zp.17] When the user has opted into activity sorting, sort
    // by last_activity_at and IGNORE sort_order. Default mode preserves
    // the original drag-to-reorder kanban behaviour exactly.
    if (activitySort === "fresh" || activitySort === "stale") {
      const dir = activitySort === "fresh" ? -1 : 1;
      return list.slice().sort((a, b) => dir * (_leadActivityTs(a) - _leadActivityTs(b)));
    }
    return list.slice().sort((a, b) => {
      const ao = a.sort_order ?? 999999;
      const bo = b.sort_order ?? 999999;
      if (ao !== bo) return ao - bo;
      return String(a.id).localeCompare(String(b.id));
    });
  };

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;
    const overId = String(over.id || "");
    const activeId = String(active.id || "");
    if (activeId === overId) return;

    // Decide whether `over` is a column drop-zone or a sibling card.
    const isOverColumn = overId.startsWith("phase-col-");
    const targetPhase = isOverColumn
      ? overId.replace("phase-col-", "")
      : (leads.find((l) => l.id === overId)?.phase || null);

    const lead = leads.find((l) => l.id === activeId);
    if (!lead || !targetPhase) return;

    const sourceList = itemsForPhase(lead.phase);
    const targetList = itemsForPhase(targetPhase);

    // Same column reorder.
    if (lead.phase === targetPhase) {
      const oldIdx = sourceList.findIndex((l) => l.id === activeId);
      let newIdx = isOverColumn
        ? sourceList.length - 1
        : sourceList.findIndex((l) => l.id === overId);
      if (oldIdx === -1 || newIdx === -1 || oldIdx === newIdx) return;
      const reordered = arrayMove(sourceList, oldIdx, newIdx);
      // Optimistic local update + persist.
      const idToOrder = new Map(reordered.map((l, i) => [l.id, i]));
      setLeads((prev) =>
        prev.map((l) => (idToOrder.has(l.id) ? { ...l, sort_order: idToOrder.get(l.id) } : l)),
      );
      persistOrder(
        reordered.map((l, i) => ({ id: l.id, sort_order: i })),
        targetPhase,
      );
      return;
    }

    // Cross-column move + position within target column.
    const insertIdx = isOverColumn
      ? targetList.length
      : Math.max(0, targetList.findIndex((l) => l.id === overId));
    const newTarget = targetList.slice();
    newTarget.splice(insertIdx, 0, { ...lead, phase: targetPhase });
    const idToOrder = new Map(newTarget.map((l, i) => [l.id, i]));
    setLeads((prev) =>
      prev.map((l) =>
        l.id === activeId
          ? { ...l, phase: targetPhase, sort_order: idToOrder.get(l.id) ?? 0 }
          : idToOrder.has(l.id)
            ? { ...l, sort_order: idToOrder.get(l.id) }
            : l,
      ),
    );
    persistOrder(
      newTarget.map((l, i) => ({ id: l.id, sort_order: i })),
      targetPhase,
    );
  };

  const convert = async (id) => {
    const { data } = await api.post(`/leads/${id}/convert`, {});
    navigate(`/jobcards/${data.id}`);
  };

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#647087]">SalesFlow pipeline</div>
          <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight mt-1 flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-[#1fb6c1] animate-pulse" />
            Leads
          </h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="flex border border-white/10 rounded-md overflow-hidden">
            <button
              onClick={() => setView("auto")}
              data-testid="leads-view-auto"
              className={`px-2.5 py-2 text-[10px] uppercase tracking-wider ${
                view === "auto" ? "bg-[#1fb6c1]/15 text-[#1fb6c1]" : "text-[#647087] hover:text-white"
              }`}
              title={`Auto · currently ${effectiveView}`}
            >
              Auto
            </button>
            <button
              onClick={() => setView("kanban")}
              data-testid="leads-view-kanban"
              className={`px-3 py-2 text-xs ${
                view === "kanban" ? "bg-[#1fb6c1]/15 text-[#1fb6c1]" : "text-[#647087] hover:text-white"
              }`}
              title="Kanban view"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setView("list")}
              data-testid="leads-view-list"
              className={`px-3 py-2 text-xs ${
                view === "list" ? "bg-[#1fb6c1]/15 text-[#1fb6c1]" : "text-[#647087] hover:text-white"
              }`}
              title="List view (grouped by phase)"
            >
              <ListIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => setView("table")}
              data-testid="leads-view-table"
              className={`px-3 py-2 text-xs ${
                view === "table" ? "bg-[#1fb6c1]/15 text-[#1fb6c1]" : "text-[#647087] hover:text-white"
              }`}
              title="Table view — Excel-grade sort & filter"
            >
              <TableIcon className="w-4 h-4" />
            </button>
          </div>
          <Link
            to="/followups"
            className="hv-btn hv-btn-ghost"
            data-testid="leads-followups-btn"
            title="Open follow-ups dashboard"
          >
            <Sparkles className="w-4 h-4 text-fuchsia-300" /> Follow-ups
            {followupStats.due > 0 && (
              <span className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-rose-500 text-white text-[10px] font-bold animate-pulse">
                {followupStats.due}
              </span>
            )}
          </Link>
          {/* [Iter 290zp.17] Lead Activity Sort — Freshest / Stalest / Default.
              Stores choice per-device. Backend persists last_activity_at on
              every movement (lead edit / phase / comment / call / meeting). */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setActivitySortMenuOpen((v) => !v)}
              onBlur={() => setTimeout(() => setActivitySortMenuOpen(false), 180)}
              className={`hv-btn ${activitySort === "default" ? "hv-btn-ghost" : "hv-btn-primary"}`}
              data-testid="leads-activity-sort-btn"
              title="Sort by latest movement on each lead"
            >
              {activitySort === "fresh" ? (
                <Flame className="w-4 h-4 text-rose-300" />
              ) : activitySort === "stale" ? (
                <Snowflake className="w-4 h-4 text-cyan-300" />
              ) : (
                <ArrowUpDown className="w-4 h-4" />
              )}
              <span className="hidden sm:inline ml-1">
                {activitySort === "fresh" ? "Freshest" : activitySort === "stale" ? "Stalest" : "Sort"}
              </span>
              <ChevronDown className="w-3 h-3 opacity-60" />
            </button>
            {activitySortMenuOpen && (
              <div
                className="absolute right-0 mt-1 z-40 w-56 bg-[#121419] border border-white/10 rounded-md shadow-2xl overflow-hidden"
                data-testid="leads-activity-sort-menu"
              >
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); setActivitySort("default"); setActivitySortMenuOpen(false); }}
                  className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-white/5 transition ${
                    activitySort === "default" ? "text-[#1fb6c1] bg-[#1fb6c1]/10" : "text-[#a0aabf]"
                  }`}
                  data-testid="leads-activity-sort-default"
                >
                  <ArrowUpDown className="w-3.5 h-3.5" />
                  <div className="flex-1">
                    <div className="font-bold uppercase tracking-wider text-[10px]">Default</div>
                    <div className="text-[10px] text-[#647087]">Per-phase drag order</div>
                  </div>
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); setActivitySort("fresh"); setActivitySortMenuOpen(false); }}
                  className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-white/5 transition border-t border-white/5 ${
                    activitySort === "fresh" ? "text-rose-300 bg-rose-500/10" : "text-[#a0aabf]"
                  }`}
                  data-testid="leads-activity-sort-fresh"
                >
                  <Flame className="w-3.5 h-3.5 text-rose-300" />
                  <div className="flex-1">
                    <div className="font-bold uppercase tracking-wider text-[10px]">Freshest first</div>
                    <div className="text-[10px] text-[#647087]">Newest movement on top</div>
                  </div>
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); setActivitySort("stale"); setActivitySortMenuOpen(false); }}
                  className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-white/5 transition border-t border-white/5 ${
                    activitySort === "stale" ? "text-cyan-300 bg-cyan-500/10" : "text-[#a0aabf]"
                  }`}
                  data-testid="leads-activity-sort-stale"
                >
                  <Snowflake className="w-3.5 h-3.5 text-cyan-300" />
                  <div className="flex-1">
                    <div className="font-bold uppercase tracking-wider text-[10px]">Stalest first</div>
                    <div className="text-[10px] text-[#647087]">Oldest movement on top</div>
                  </div>
                </button>
              </div>
            )}
          </div>
          <Link
            to="/tools/onboarding?new=prospect"
            className="hv-btn hv-btn-ghost"
            data-testid="lead-new-prospect-btn"
            title="Start the detailed water-source / power / occupancy fact-find"
          >
            <Plus className="w-4 h-4" /> Lead onboarding
          </Link>
          <Link
            to="/suspects"
            className="hv-btn hv-btn-ghost"
            data-testid="lead-suspects-link"
            title="Top-of-funnel suspects (xlsx-style capture)"
          >
            <UserPlus className="w-4 h-4 text-[#A78BFA]" /> Suspects
          </Link>
          
          <button onClick={() => setMeetingOpen({ leadId: null, customerId: null })} className="hv-btn hv-btn-ghost" data-testid="lead-new-meeting-btn">
            <CalendarPlus className="w-4 h-4 text-[#1FB6C1]" /> Meeting
          </button>
          <button
            onClick={() => setDiscoverOpen(true)}
            className="hv-btn hv-btn-ghost"
            data-testid="lead-discover-btn"
            title="Discover new prospects near any area via Google Places"
          >
            <Globe className="w-4 h-4 text-[#A78BFA]" /> Discover
          </button>
          {/* Iter 230 — Deep Research + Trip Planner surface here instead
              of cluttering the sidebar. Both are SalesFlow capabilities
              that reps reach contextually from the Leads board. */}
          <Link
            to="/research"
            className="hv-btn hv-btn-ghost"
            data-testid="lead-deep-research-link"
            title="Deep multi-source research on a lead or area"
          >
            <FlaskConical className="w-4 h-4 text-[#22D3EE]" /> Deep research
          </Link>
          <Link
            to="/trips"
            className="hv-btn hv-btn-ghost"
            data-testid="lead-trip-planner-link"
            title="Plan a route to visit leads + customers"
          >
            <MapPin className="w-4 h-4 text-[#F59E0B]" /> Plan a trip
          </Link>
          <button onClick={() => setOpen(true)} className="hv-btn hv-btn-primary" data-testid="lead-new-btn">
            <Plus className="w-4 h-4" /> New lead
          </button>
        </div>
      </div>

      {/* Global search bar */}
      <div className="hv-card px-3 py-2.5">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#647087]" />
          <input
            value={globalSearch}
            onChange={(e) => setGlobalSearch(e.target.value)}
            placeholder="Search any lead — name, contact, customer, phase, source, town…"
            className="hv-input !pl-9 text-sm"
            data-testid="leads-global-search"
          />
          {globalSearch && (
            <button
              type="button"
              onClick={() => setGlobalSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[#647087] hover:text-white p-1"
              data-testid="leads-global-search-clear"
            ><X className="w-3.5 h-3.5" /></button>
          )}
        </div>
      </div>

      {/* Follow-ups thin banner */}
      <Link
        to="/followups"
        data-testid="leads-followups-banner"
        className="block group"
      >
        <div
          className={`hv-card px-4 py-2.5 flex items-center justify-between gap-3 transition border ${
            followupStats.due > 0
              ? "border-rose-500/40 bg-rose-500/5 hover:bg-rose-500/10"
              : followupStats.in7 > 0
              ? "border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10"
              : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"
          }`}
        >
          <div className="flex items-center gap-2 text-xs">
            <Sparkles className="w-4 h-4 text-fuchsia-300 flex-shrink-0" />
            <span className="uppercase tracking-wider text-[#a0aabf] font-bold">
              Follow-ups
            </span>
            <span className="text-[#647087] hidden sm:inline">·</span>
            <span className="text-[#647087] hidden sm:inline">
              {followupStats.total === 0
                ? "No pending steps yet — move a lead to Future-Prospect to seed."
                : "Click to open dashboard"}
            </span>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <Bucket
              icon={AlertTriangle}
              count={followupStats.due}
              label="Due now"
              color="#F43F5E"
              pulse
            />
            <Bucket
              icon={Clock}
              count={followupStats.in7}
              label="7 days"
              color="#F59E0B"
            />
            <Bucket
              icon={Clock}
              count={followupStats.in30}
              label="30 days"
              color="#A78BFA"
            />
            <ArrowRight className="w-4 h-4 text-[#647087] group-hover:text-white group-hover:translate-x-0.5 transition" />
          </div>
        </div>
      </Link>

      {/* Group banners + columns */}
      {effectiveView === "kanban" ? (
        <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={onDragEnd}>
        <div className="overflow-x-auto pb-4">
        <div className="flex flex-col gap-2 min-w-[1700px]">
          {/* Group header strip */}
          <div className="flex gap-3">
            {PHASE_GROUPS.map((g) => (
              <div
                key={g.group}
                className="rounded-md border px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-bold"
                style={{
                  width: `${g.phases.length * 18 + (g.phases.length - 1) * 0.75}rem`,
                  flexShrink: 0,
                  borderColor: `${g.accent}40`,
                  background: `${g.accent}1a`,
                  color: g.accent,
                }}
              >
                {g.group} · {g.phases.length}
              </div>
            ))}
          </div>

          {/* Columns */}
          <div className="flex gap-3">
            {PHASE_GROUPS.flatMap((g) =>
              g.phases.map((p, idx) => {
                const items = itemsForPhase(p.key);
                const isLastInGroup = idx === g.phases.length - 1;
                return (
                  <DroppableColumn
                    key={p.key}
                    phase={p}
                    group={g}
                    items={items}
                    isLastInGroup={isLastInGroup}
                    customers={customers}
                    onPick={(l) => navigate(`/leads/${l.id}`)}
                    onMove={move}
                    onMeeting={(l) => setMeetingOpen({ leadId: l.id, customerId: l.customer_id })}
                  />
                );
              })
            )}
          </div>
        </div>
      </div>
      </DndContext>
      ) : effectiveView === "table" ? (
        <LeadsTableView
          leads={visibleLeads}
          customers={customers}
          phaseGroups={PHASE_GROUPS}
          onMeeting={(lead) => setMeetingOpen({ leadId: lead.id, customerId: lead.customer_id })}
          isUnseen={isUnseen}
          dismissUnseen={dismiss}
        />
      ) : (
        <LeadsListView
          leads={visibleLeads}
          customers={customers}
          phaseGroups={PHASE_GROUPS}
          onMove={move}
          onConvert={convert}
          onOpen={(l) => navigate(`/leads/${l.id}`)}
          onMeeting={(lead) => setMeetingOpen({ leadId: lead.id, customerId: lead.customer_id })}
          activitySort={activitySort}
        />
      )}

      {/* Meeting modal — used both for global "+ Meeting" and per-lead inline */}
      <Modal
        open={!!meetingOpen}
        onClose={() => setMeetingOpen(null)}
        title={meetingOpen?.leadId ? "Schedule meeting for lead" : "Schedule meeting"}
        testId="lead-meeting-modal"
        maxWidth="max-w-2xl"
      >
        <MeetingForm
          defaultLeadId={meetingOpen?.leadId || ""}
          defaultCustomerId={meetingOpen?.customerId || ""}
          onCreated={() => { setMeetingOpen(null); load(); }}
          onCancel={() => setMeetingOpen(null)}
        />
      </Modal>

      <Modal open={open} onClose={() => { setOpen(false); setEditingLead(null); }} title={editingLead ? `Edit lead — ${editingLead.title}` : "New lead"} testId="lead-modal" maxWidth="max-w-3xl">
        <LeadEditor
          value={editingLead || {}}
          onSaved={() => { setOpen(false); setEditingLead(null); load(); }}
          onCancel={() => { setOpen(false); setEditingLead(null); }}
        />
      </Modal>

      {discoverOpen && (
        <DiscoverProspectsDialog
          onClose={() => setDiscoverOpen(false)}
          onCommitted={() => load()}
        />
      )}

      {/* Lead detail modal with call log */}
      {activeLead && (
        <LeadDetailModal
          lead={activeLead}
          customers={customers}
          phaseGroups={PHASE_GROUPS}
          onClose={() => setActiveLead(null)}
          onChanged={() => {
            setActiveLead(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function LeadDetailModal({ lead, customers, phaseGroups, onClose, onChanged }) {
  const navigate = useNavigate();
  // ... (rest of the original body remains unchanged below)
  const [contacts, setContacts] = useState([]);
  const [jobcards, setJobcards] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [phase, setPhase] = useState(lead.phase);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const customer = customers.find((c) => c.id === lead.customer_id) || null;

  useEffect(() => {
    const run = async () => {
      // Comments load even for prospects (no customer)
      const cm = await api.get(`/lead-comments?lead_id=${lead.id}`).catch(() => ({ data: [] }));
      setComments(cm.data || []);
      if (!lead.customer_id) return;
      const [j, t, c] = await Promise.all([
        api.get(`/jobcards?customer_id=${lead.customer_id}`).catch(() => ({ data: [] })),
        api.get(`/tickets?customer_id=${lead.customer_id}`).catch(() => ({ data: [] })),
        api.get(`/contacts?customer_id=${lead.customer_id}`).catch(() => ({ data: [] })),
      ]);
      setJobcards(Array.isArray(j.data) ? j.data : []);
      setTickets(Array.isArray(t.data) ? t.data : []);
      setContacts(Array.isArray(c.data) ? c.data : []);
    };
    run();
  }, [lead.id, lead.customer_id]);

  const addComment = async () => {
    const txt = (newComment || "").trim();
    if (!txt) return;
    await api.post(`/lead-comments`, { lead_id: lead.id, text: txt });
    setNewComment("");
    const cm = await api.get(`/lead-comments?lead_id=${lead.id}`);
    setComments(cm.data || []);
  };

  const updatePhase = async (p) => {
    setPhase(p);
    await api.patch(`/leads/${lead.id}/phase`, { phase: p });
  };

  const convert = async () => {
    const { data } = await api.post(`/leads/${lead.id}/convert`, {});
    onChanged();
    navigate(`/jobcards/${data.id}`);
  };

  const promoteToCustomer = async () => {
    // Auto-create a customer from this lead's prospect fields
    const cust = await api.post("/customers", {
      customer_type: "individual",
      name: lead.customer_name || lead.title,
      phone: lead.customer_phone || "",
      whatsapp: lead.customer_phone || "",
      email: lead.customer_email || "",
      status: "active",
    });
    await api.put(`/leads/${lead.id}`, { ...lead, customer_id: cust.data.id });
    onChanged();
  };

  return (
    <Modal open onClose={onClose} title={lead.title} testId="lead-detail-modal" maxWidth="max-w-3xl">
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-sm text-[#a0aabf]">
            {customer ? (
              <span>
                Customer:{" "}
                <span className="text-[#1fb6c1]">{customer.name}</span>
              </span>
            ) : (
              <span>Prospect: <span className="text-[#a78bfa]">{lead.customer_name || "—"}</span></span>
            )}
            {lead.value > 0 && (
              <span className="ml-3 text-[#1fb6c1]">R{Number(lead.value).toLocaleString("en-GB")}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                onClose();
                // Re-open the same lead with the editor flag — works with the
                // existing CreateLeadModal/EditLeadModal flow elsewhere in this file.
                navigate(`/leads?edit=${lead.id}`);
              }}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="lead-detail-edit-btn"
            >
              <Pencil className="w-3.5 h-3.5" /> Edit
            </button>
            <button onClick={onClose} className="text-[#647087] hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Phase</label>
            <select
              value={phase}
              onChange={(e) => updatePhase(e.target.value)}
              className="hv-input mt-1"
              data-testid="lead-detail-phase"
            >
              {phaseGroups.map((grp) => (
                <optgroup key={grp.group} label={grp.group}>
                  {grp.phases.map((p) => (
                    <option key={p.key} value={p.key}>
                      {p.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <div className="flex items-end">
          </div>
        </div>

        {lead.notes && (
          <div className="border border-white/10 rounded-md p-3 bg-white/[0.02] text-sm whitespace-pre-wrap">
            {lead.notes}
          </div>
        )}

        {/* Phase A: Lead meta chips (always rendered for testability; shows placeholder when empty) */}
        <div className="flex items-center gap-2 flex-wrap text-[10px] uppercase tracking-wider" data-testid="lead-meta-chips">
          {lead.external_ref && (
            <span className="px-2 py-0.5 rounded border border-[#1fb6c1]/30 text-[#1fb6c1] bg-[#1fb6c1]/5 font-mono">
              {lead.external_ref}
            </span>
          )}
          {lead.lead_source && (
              <span className="px-2 py-0.5 rounded border border-white/10 text-[#a0aabf] bg-white/5">
                Source · {lead.lead_source.trim()}
              </span>
            )}
            {lead.lead_rating && (
              <span className="px-2 py-0.5 rounded border border-amber-500/40 text-amber-300 bg-amber-500/10 inline-flex items-center gap-1">
                <Star className="w-3 h-3 fill-current" /> {lead.lead_rating} / 5
              </span>
            )}
            {Array.isArray(lead.service_interest) && lead.service_interest.length > 0 && (
              <span className="px-2 py-0.5 rounded border border-violet-500/30 text-violet-300 bg-violet-500/10">
                {lead.service_interest.join(" · ")}
              </span>
            )}
            {lead.first_contact_date && (
              <span className="px-2 py-0.5 rounded border border-white/10 text-[#a0aabf] bg-white/5">
                First contact · {new Date(lead.first_contact_date).toLocaleDateString('en-GB')}
              </span>
            )}
            {lead.next_followup_date && (
              <span className="px-2 py-0.5 rounded border border-rose-500/30 text-rose-300 bg-rose-500/10">
                Next follow-up · {new Date(lead.next_followup_date).toLocaleDateString('en-GB')}
              </span>
            )}
            {lead.lead_title_enum && (
              <span className="px-2 py-0.5 rounded border border-cyan-500/30 text-cyan-300 bg-cyan-500/10">
                {lead.lead_title_enum}
              </span>
            )}
            {lead.price_bracket && (
              <span className="px-2 py-0.5 rounded border border-emerald-500/30 text-emerald-300 bg-emerald-500/10">
                {PRICE_BRACKET_LABELS[lead.price_bracket] || lead.price_bracket}
              </span>
            )}
            {lead.current_provider && (
              <span className="px-2 py-0.5 rounded border border-amber-500/30 text-amber-300 bg-amber-500/10">
                vs. {lead.current_provider}
              </span>
            )}
            {!(lead.lead_source || lead.lead_rating || lead.service_interest?.length || lead.first_contact_date || lead.next_followup_date || lead.external_ref || lead.lead_title_enum || lead.price_bracket || lead.current_provider) && (
              <span className="text-[#647087] normal-case tracking-normal">No metadata yet — fill source / rating on the lead form.</span>
            )}
        </div>

        {/* Wave 4: Onboarding form launcher */}
        <div className="hv-card p-3 flex items-center gap-3 flex-wrap" data-testid="lead-onboarding-launcher">
          <div className="min-w-0 flex-1">
            <div className="text-xs uppercase tracking-wider text-[#1fb6c1] font-bold">Prospect onboarding form</div>
            <div className="text-[11px] text-[#a0aabf] mt-0.5">
              {lead.onboarding ? (
                <span className="text-emerald-300">Info on file · always editable</span>
              ) : (
                "Capture full site survey, water quality, power, pumps, etc."
              )}
            </div>
          </div>
          <Link
            to={`/leads/${lead.id}/onboarding`}
            className="hv-btn hv-btn-primary text-xs"
            data-testid="lead-open-onboarding"
          >
            {lead.onboarding ? "Open form" : "Start onboarding →"}
          </Link>
        </div>

        {/* Phase A: Lead comments thread */}
        <div className="hv-card p-4 space-y-3" data-testid="lead-comments-section">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-[#1fb6c1]" />
            <div className="text-xs uppercase tracking-[0.2em] text-[#a0aabf] font-bold">
              Lead comments {comments.length > 0 && <span className="text-[#1fb6c1]">· {comments.length}</span>}
            </div>
          </div>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {comments.length === 0 && (
              <div className="text-xs text-[#647087]">No comments yet — drop the first one below.</div>
            )}
            {comments.map((cm) => (
              <div key={cm.id} className="border border-white/5 rounded-md p-2 bg-white/[0.02]" data-testid={`lead-comment-${cm.id}`}>
                <div className="text-xs text-[#a0aabf] flex items-center gap-2">
                  <span className="text-[#1fb6c1] font-bold">{cm.by_user_name || "—"}</span>
                  <span className="text-[#647087]">{new Date(cm.created_at).toLocaleString('en-GB')}</span>
                </div>
                <div className="text-sm whitespace-pre-wrap mt-1">{cm.text}</div>
              </div>
            ))}
          </div>
          <div className="flex items-end gap-2">
            <VoiceTextarea
              value={newComment}
              onChange={setNewComment}
              placeholder="Add a comment…"
              data-testid="lead-comment-input"
             testIdPrefix="lead-comment-input-voice" context="note" />
            <button
              onClick={addComment}
              disabled={!newComment.trim()}
              className="hv-btn hv-btn-primary"
              data-testid="lead-comment-submit"
            >
              Post
            </button>
          </div>
        </div>

        {customer ? (
          <CallLog
            customerId={customer.id}
            leads={[lead]}
            jobcards={jobcards}
            tickets={tickets}
            contacts={contacts}
          />
        ) : (
          <div className="hv-card p-5 text-sm text-[#a0aabf] space-y-3">
            <div>
              <span className="text-[#a78bfa] font-bold">Prospect not yet a customer.</span> Promote
              this prospect to start logging calls, meetings and notes against them — so the next
              rep picks up exactly where you left off.
            </div>
            <button
              onClick={promoteToCustomer}
              className="hv-btn hv-btn-primary"
              data-testid="lead-promote-btn"
            >
              Promote prospect to customer
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}

function Bucket({ icon: Icon, count, label, color, pulse }) {
  const dim = count === 0;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold border ${
        pulse && !dim ? "animate-pulse" : ""
      }`}
      style={{
        borderColor: dim ? "rgba(255,255,255,0.1)" : `${color}55`,
        background: dim ? "transparent" : `${color}1a`,
        color: dim ? "#647087" : color,
      }}
    >
      <Icon className="w-3 h-3" />
      <span className="font-bold">{count}</span> {label}
    </span>
  );
}

/* ---------------- Leads list view (grouped by status) ---------------- */
function LeadsListView({ leads, customers, phaseGroups, onMove, onConvert, onOpen, onMeeting, activitySort }) {
  const customerName = (id) => customers.find((c) => c.id === id)?.name;
  const [collapsed, setCollapsed] = usePersistedState("leads.list.collapsed", {});
  const visibleLeads = leads;
  const toggle = (group) => setCollapsed({ ...collapsed, [group]: !collapsed[group] });
  // [Iter 290zp.17] Lead Activity Sort — apply the freshness sort within
  // each sub-phase folder. Default keeps natural ordering.
  const _ts = (l) => {
    const v = l?.last_activity_at || l?.updated_at || l?.created_at;
    if (!v) return 0;
    const t = Date.parse(v);
    return Number.isFinite(t) ? t : 0;
  };
  const sortPhaseLeads = (rows) => {
    if (activitySort === "fresh") return rows.slice().sort((a, b) => _ts(b) - _ts(a));
    if (activitySort === "stale") return rows.slice().sort((a, b) => _ts(a) - _ts(b));
    return rows;
  };
  return (
    <div className="space-y-5" data-testid="leads-list-view">
      {phaseGroups.map((g) => {
        const groupLeads = visibleLeads.filter((l) =>
          g.phases.some((p) =>
            p.key === l.phase || (l.secondary_phases || []).includes(p.key),
          ),
        );
        const isCollapsed = !!collapsed[g.group];
        return (
          <div key={g.group} className="space-y-2">
            {/* Group header banner — clickable to fold/unfold */}
            <button
              type="button"
              onClick={() => toggle(g.group)}
              className="w-full flex items-center justify-between px-3 py-2 rounded-md border transition hover:brightness-125"
              style={{
                borderColor: `${g.accent}55`,
                background: `${g.accent}12`,
              }}
              data-testid={`leads-list-group-${g.group}`}
              aria-expanded={!isCollapsed}
            >
              <div className="flex items-center gap-2">
                <span
                  className="w-1.5 h-6 rounded-full"
                  style={{ background: g.accent }}
                />
                <ChevronDown
                  className={`w-4 h-4 transition ${isCollapsed ? "-rotate-90" : ""}`}
                  style={{ color: g.accent }}
                />
                <div className="text-left">
                  <div
                    className="text-xs uppercase tracking-[0.25em] font-bold"
                    style={{ color: g.accent }}
                  >
                    {g.group}
                  </div>
                  <div className="text-[10px] text-[#647087] uppercase tracking-wider">
                    {groupLeads.length} {groupLeads.length === 1 ? "lead" : "leads"}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {!isCollapsed && g.phases.map((p) => {
                  const n = visibleLeads.filter(
                    (l) =>
                      l.phase === p.key ||
                      (l.secondary_phases || []).includes(p.key),
                  ).length;
                  if (n === 0) return null;
                  return (
                    <span
                      key={p.key}
                      className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border"
                      style={{
                        borderColor: `${p.color}55`,
                        background: `${p.color}1a`,
                        color: p.color,
                      }}
                    >
                      {p.label} <span className="font-bold">·{n}</span>
                    </span>
                  );
                })}
              </div>
            </button>

            {/* Group rows */}
            {!isCollapsed && (groupLeads.length === 0 ? (
              <div className="hv-card p-5 text-center text-xs text-[#647087]">
                No leads in this status group.
              </div>
            ) : (
              <div className="space-y-2">
                {/* Wave 7-fix #11 — sub-phase folders inside each Open/Quoting/Closed band */}
                {g.phases.map((p) => {
                  const phaseLeads = sortPhaseLeads(groupLeads.filter((l) => l.phase === p.key));
                  if (phaseLeads.length === 0) return null;
                  const phaseKey = `${g.group}__${p.key}`;
                  const phaseCollapsed = !!collapsed[phaseKey];
                  return (
                    <div key={p.key} className="hv-card overflow-hidden" data-testid={`leads-subphase-${p.key}`}>
                      <button
                        type="button"
                        onClick={() => toggle(phaseKey)}
                        className="w-full flex items-center justify-between px-4 py-2 hover:brightness-125 transition"
                        style={{ background: `${p.color}10`, borderBottom: phaseCollapsed ? "none" : `1px solid ${p.color}30` }}
                        aria-expanded={!phaseCollapsed}
                      >
                        <div className="flex items-center gap-2">
                          <ChevronRight
                            className={`w-3.5 h-3.5 transition ${phaseCollapsed ? "" : "rotate-90"}`}
                            style={{ color: p.color }}
                          />
                          <span className="text-xs uppercase tracking-wider font-bold" style={{ color: p.color }}>
                            {p.label}
                          </span>
                        </div>
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded-full border" style={{ color: p.color, borderColor: `${p.color}55`, background: `${p.color}10` }}>
                          {phaseLeads.length}
                        </span>
                      </button>
                      {!phaseCollapsed && (
                        <div className="overflow-x-auto -mx-1">
                        <table className="w-full text-sm min-w-[360px]">
                          <tbody>
                            {phaseLeads.map((l) => (
                              <tr
                                key={l.id}
                                onClick={() => onOpen(l)}
                                data-testid={`lead-row-${l.id}`}
                                className="border-b border-white/5 hover:bg-[#1fb6c1]/5 transition cursor-pointer"
                                style={{ borderLeft: `3px solid ${p.color}` }}
                              >
                                <td className="px-3 py-2.5 font-medium">
                                  <div className="truncate max-w-[40vw] sm:max-w-none">{l.title}</div>
                                  {/* Customer + value shown inline under title on mobile only */}
                                  <div className="text-[10px] text-[#647087] sm:hidden mt-0.5 truncate">
                                    {customerName(l.customer_id) || l.customer_name || "prospect"}
                                    {l.value > 0 && <span className="text-[#1fb6c1] ml-2">${l.value.toLocaleString('en-GB')}</span>}
                                  </div>
                                </td>
                                <td className="px-3 py-2.5 text-[#a0aabf] hidden sm:table-cell">
                                  {customerName(l.customer_id) || l.customer_name || <span className="text-[#647087] italic">prospect</span>}
                                </td>
                                <td className="px-3 py-2.5 text-[#1fb6c1] font-mono text-xs hidden md:table-cell">
                                  {l.value > 0 ? `$${l.value.toLocaleString('en-GB')}` : "—"}
                                </td>
                                <td className="px-3 py-2.5 text-[#647087] text-xs whitespace-nowrap hidden lg:table-cell" data-testid={`lead-row-activity-${l.id}`}>
                                  {(() => {
                                    const ts = _ts(l);
                                    if (!ts) return "—";
                                    const ago = Date.now() - ts;
                                    const day = 86400000;
                                    const d = Math.floor(ago / day);
                                    if (d < 1) return "today";
                                    if (d < 7) return `${d}d ago`;
                                    if (d < 30) return `${Math.floor(d / 7)}w ago`;
                                    if (d < 365) return `${Math.floor(d / 30)}mo ago`;
                                    return `${Math.floor(d / 365)}y ago`;
                                  })()}
                                </td>
                                <td className="px-2 py-2 whitespace-nowrap w-0" onClick={(e) => e.stopPropagation()}>
                                  {/* Iter 397a — mobile: keep controls on a single row instead of wrapping into
                                      a vertical stack. Shrink the phase select to ~52px on mobile so all 3 controls
                                      fit next to the truncated name column. `flex-nowrap` prevents the flex parent
                                      from letting the select overflow-push the buttons to the next line. */}
                                  <div className="flex flex-nowrap items-center justify-end gap-1">
                                    <select
                                      value={l.phase}
                                      onChange={(e) => onMove(l.id, e.target.value)}
                                      className="hv-input text-[10px] py-1 px-1 w-14 sm:w-24 shrink-0"
                                      data-testid={`lead-phase-${l.id}`}
                                      title={phaseGroups.flatMap((gg) => gg.phases).find((pp) => pp.key === l.phase)?.label || l.phase}
                                    >
                                      {phaseGroups.map((grp) => (
                                        <optgroup key={grp.group} label={grp.group}>
                                          {grp.phases.map((pp) => (
                                            <option key={pp.key} value={pp.key}>
                                              {pp.label}
                                            </option>
                                          ))}
                                        </optgroup>
                                      ))}
                                    </select>
                                    {onMeeting && (
                                      <button
                                        type="button"
                                        onClick={() => onMeeting(l)}
                                        title="Schedule meeting / callout"
                                        className="hv-btn hv-btn-ghost text-[10px] px-1.5 py-1 shrink-0"
                                        data-testid={`lead-row-meeting-${l.id}`}
                                      >
                                        <CalendarPlus className="w-3 h-3 text-[#A78BFA]" />
                                      </button>
                                    )}
                                    
                                    {l.customer_phone && (
                                      <a
                                        href={`https://wa.me/${(l.customer_phone || "").replace(/\D/g, "")}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        title={`WhatsApp ${l.customer_phone}`}
                                        className="hv-btn hv-btn-ghost text-[10px] px-1.5 py-1 shrink-0"
                                        data-testid={`lead-row-whatsapp-${l.id}`}
                                      >
                                        <MessageSquare className="w-3 h-3 text-[#22C55E]" />
                                      </a>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function DroppableColumn({ phase, group, items, isLastInGroup, customers, onPick, onMove, onMeeting }) {
  const { setNodeRef, isOver } = useDroppable({ id: `phase-col-${phase.key}` });
  const ids = items.map((l) => l.id);
  return (
    <div
      className="w-72 flex-shrink-0"
      data-testid={`lead-col-${phase.key}`}
      style={isLastInGroup ? { marginRight: "0" } : {}}
    >
      <div
        className="flex items-center justify-between px-2 py-2 rounded-t-md"
        style={{ borderBottom: `2px solid ${group.accent}` }}
      >
        <div className="text-xs uppercase tracking-[0.18em] text-white/90 font-bold leading-tight">
          {phase.label}
        </div>
        <span
          className="text-[10px] font-bold px-1.5 py-0.5 rounded"
          style={{ background: `${group.accent}26`, color: group.accent }}
        >
          {items.length}
        </span>
      </div>
      <div
        ref={setNodeRef}
        className="bg-[#0a0b0e] border border-t-0 rounded-b-md p-2 min-h-[200px] space-y-2 transition-colors"
        style={{ borderColor: isOver ? group.accent : "rgba(255,255,255,0.05)", background: isOver ? `${group.accent}10` : "#0a0b0e" }}
        data-testid={`lead-col-drop-${phase.key}`}
      >
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {items.map((l) => (
            <DraggableLeadCard
              key={l.id}
              lead={l}
              customers={customers}
              onPick={onPick}
              onMove={onMove}
              onMeeting={onMeeting}
            />
          ))}
        </SortableContext>
        {items.length === 0 && (
          <div className="text-xs text-[#647087] p-3 text-center">No leads.</div>
        )}
      </div>
    </div>
  );
}

function DraggableLeadCard({ lead, customers, onPick, onMove, onMeeting }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: lead.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 10 : "auto",
  };
  const l = lead;
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="hv-card p-3 hover:border-[#1fb6c1]/40 transition"
      data-testid={`lead-card-${l.id}`}
    >
      <div className="flex items-start gap-1.5">
        <span
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-[#647087] hover:text-white p-0.5 -ml-1 shrink-0 mt-0.5"
          data-testid={`lead-card-handle-${l.id}`}
          title="Drag to reorder or move to a different phase"
        >
          <GripVertical className="w-3.5 h-3.5" />
        </span>
        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onPick(l)}>
          <div className="font-medium leading-tight truncate">{l.title}</div>
          <div className="text-xs text-[#647087] mt-1 truncate">
            {l.customer_name || customers.find((c) => c.id === l.customer_id)?.name || "—"}
          </div>
          {l.value > 0 && <div className="text-xs text-[#1fb6c1] mt-1">${l.value}</div>}
          {(() => {
            // [Iter 290zp.17] Activity freshness chip on each kanban card.
            // [Iter 400.7] Also shows WHO made the last move + WHAT the
            // move was, so a manager can see rep activity at a glance.
            const v = l?.last_activity_at || l?.updated_at || l?.created_at;
            if (!v) return null;
            const t = Date.parse(v);
            if (!Number.isFinite(t)) return null;
            const ago = Date.now() - t;
            const day = 86400000;
            const d = Math.floor(ago / day);
            let label;
            if (d < 1) label = "today";
            else if (d < 7) label = `${d}d`;
            else if (d < 30) label = `${Math.floor(d / 7)}w`;
            else if (d < 365) label = `${Math.floor(d / 30)}mo`;
            else label = `${Math.floor(d / 365)}y`;
            // Tone: red for stale (>30d), amber for 7-30d, neutral otherwise.
            let color = "text-[#647087] border-white/10 bg-white/[0.03]";
            if (d >= 30) color = "text-rose-300 border-rose-500/30 bg-rose-500/10";
            else if (d >= 7) color = "text-amber-300 border-amber-500/30 bg-amber-500/10";
            const by = l?.last_activity_by_name;
            const kind = l?.last_activity_kind;
            const detail = [kind, by ? `by ${by}` : null].filter(Boolean).join(" · ");
            return (
              <div
                className={`text-[9px] uppercase tracking-wider mt-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border ${color}`}
                title={`Last movement: ${new Date(t).toLocaleString("en-GB")}${detail ? ` — ${detail}` : ""}`}
                data-testid={`lead-card-activity-${l.id}`}
              >
                <Activity className="w-2.5 h-2.5" /> {label}
                {by && (
                  <span className="normal-case tracking-normal text-[9px] opacity-80 truncate max-w-[80px]">
                    · {by.split(" ")[0]}
                  </span>
                )}
              </div>
            );
          })()}
          {(l.secondary_phases || []).length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {(l.secondary_phases || []).map((sp) => {
                const meta = ALL_PHASES.find((pp) => pp.key === sp);
                if (!meta) return null;
                return (
                  <span
                    key={sp}
                    className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border"
                    style={{
                      borderColor: `${meta.color}55`,
                      background: `${meta.color}1a`,
                      color: meta.color,
                    }}
                    title={`Concurrent phase: ${meta.label}`}
                    data-testid={`lead-secondary-${l.id}-${sp}`}
                  >+ {meta.label}</span>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 mt-3" onClick={(e) => e.stopPropagation()}>
        <select
          value={l.phase}
          onChange={(e) => onMove(l.id, e.target.value)}
          onPointerDown={(e) => e.stopPropagation()}
          className="hv-input text-[10px] flex-1 py-1 px-1 min-w-0"
          data-testid={`lead-phase-${l.id}`}
        >
          {PHASE_GROUPS.map((grp) => (
            <optgroup key={grp.group} label={grp.group}>
              {grp.phases.map((ph) => (
                <option key={ph.key} value={ph.key}>{ph.label}</option>
              ))}
            </optgroup>
          ))}
        </select>
        <button
          onClick={(e) => { e.stopPropagation(); onMeeting(l); }}
          onPointerDown={(e) => e.stopPropagation()}
          className="hv-btn hv-btn-ghost px-1.5 py-1 text-xs shrink-0"
          title="Schedule a meeting / callout"
          data-testid={`lead-meeting-${l.id}`}
        >
          <CalendarPlus className="w-3 h-3 text-[#A78BFA]" />
        </button>
        <div
          onPointerDown={(e) => e.stopPropagation()}
          className="shrink-0"
        >
          
        </div>
        {l.customer_phone && (
          <a
            href={`https://wa.me/${(l.customer_phone || "").replace(/\D/g, "")}`}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            className="hv-btn hv-btn-ghost px-1.5 py-1 text-xs shrink-0"
            title="WhatsApp"
            data-testid={`lead-whatsapp-${l.id}`}
          >
            <MessageSquare className="w-3 h-3 text-[#22C55E]" />
          </a>
        )}
      </div>
    </div>
  );
}

function CompetitorPicker({ value, onChange }) {
  const [options, setOptions] = useState([]);
  const [query, setQuery] = useState(value || "");
  const [openList, setOpenList] = useState(false);

  useEffect(() => {
    let cancel = false;
    api.get(`/competitors${query ? `?q=${encodeURIComponent(query)}` : ""}`).then((r) => {
      if (!cancel) setOptions(r.data || []);
    });
    return () => {
      cancel = true;
    };
  }, [query]);

  const pick = (name) => {
    onChange(name);
    setQuery(name);
    setOpenList(false);
  };

  return (
    <div className="relative" data-testid="lead-competitor-picker">
      <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Current service provider (competitor)</label>
      <input
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          onChange(e.target.value);
          setOpenList(true);
        }}
        onFocus={() => setOpenList(true)}
        onBlur={() => setTimeout(() => setOpenList(false), 200)}
        placeholder="Search or type a new provider…"
        className="hv-input mt-1 w-full"
        data-testid="lead-input-competitor"
      />
      {openList && options.length > 0 && (
        <div className="absolute z-30 mt-1 w-full bg-[#121419] border border-white/10 rounded-md shadow-xl max-h-48 overflow-y-auto">
          {options.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => pick(c.name)}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-white/5 flex justify-between items-center"
              data-testid={`lead-competitor-option-${c.id}`}
            >
              <span>{c.name}</span>
              <span className="text-[10px] text-[#647087] font-mono">{c.usage_count || 0}×</span>
            </button>
          ))}
        </div>
      )}
      <div className="text-[10px] text-[#647087] mt-1">
        Auto-saves to the competitors list — appears in future searches.
      </div>
    </div>
  );
}

export { ALL_PHASES, PHASE_GROUPS };
